import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useMutation, useQuery } from "@apollo/client";
import AdminLoading from "../Components/AdminLoading";
import ConfirmDialog from "../Components/ConfirmDialog";

import {
  ADMIN_NEWSLETTER_BANNERS_QUERY,
  CREATE_NEWSLETTER_BANNER_MUTATION,
  UPDATE_NEWSLETTER_BANNER_MUTATION,
  DELETE_NEWSLETTER_BANNER_MUTATION,
} from "../../graphql/newsletter";

import NewsletterBannerFormDialog, {
  NewsletterBannerForm,
} from "../Components/NewsletterBannerFormDialog";

import { buildNewsletterBannerImageUrl } from "../../api/newsletterBanner";

const emptyBanner: NewsletterBannerForm = {
  id: "",
  image: "",
  updatedAt: "",
};

const NewsletterBannerAdminPage: React.FC = () => {
  const { data, loading, error } = useQuery(ADMIN_NEWSLETTER_BANNERS_QUERY);

  const [createBanner, { loading: creating }] = useMutation(
    CREATE_NEWSLETTER_BANNER_MUTATION,
    { refetchQueries: [{ query: ADMIN_NEWSLETTER_BANNERS_QUERY }] },
  );

  const [updateBanner, { loading: updating }] = useMutation(
    UPDATE_NEWSLETTER_BANNER_MUTATION,
    { refetchQueries: [{ query: ADMIN_NEWSLETTER_BANNERS_QUERY }] },
  );

  const [deleteBanner, { loading: deleting }] = useMutation(
    DELETE_NEWSLETTER_BANNER_MUTATION,
    { refetchQueries: [{ query: ADMIN_NEWSLETTER_BANNERS_QUERY }] },
  );

  const currentBanner: NewsletterBannerForm | null = useMemo(() => {
    const list: NewsletterBannerForm[] = data?.newsletterBanners || [];
    if (!list.length) return null;

    const sorted = [...list].sort((a, b) => {
      const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return tb - ta;
    });

    const b = sorted[0];
    return {
      id: b.id,
      image: b.image || "",
      updatedAt: b.updatedAt || "",
    };
  }, [data]);

  const hasBanner = !!currentBanner?.id;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<NewsletterBannerForm>(emptyBanner);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<NewsletterBannerForm | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);

  const busy = loading || creating || updating || deleting;

  const openUploadOrEdit = () => {
    setActionError(null);

    if (currentBanner) {
      setForm(currentBanner); // edit existing
    } else {
      setForm(emptyBanner); // upload new
    }

    setDialogOpen(true);
  };

  const handleSave = async (imagePath: string) => {
    setActionError(null);

    try {
      if (!form.id) {
        const res = await createBanner({ variables: { image: imagePath } });

        const errors = res.data?.createNewsletterBanner?.errors || [];
        if (errors.length) {
          setActionError(errors.join(", "));
          return;
        }
      } else {
        const res = await updateBanner({
          variables: { id: form.id, image: imagePath },
        });

        const errors = res.data?.updateNewsletterBanner?.errors || [];
        if (errors.length) {
          setActionError(errors.join(", "));
          return;
        }
      }

      setDialogOpen(false);
      setForm(emptyBanner);
    } catch (e: any) {
      setActionError(e.message || "Failed to save banner");
    }
  };

  const askDelete = () => {
    if (!currentBanner) return;
    setToDelete(currentBanner);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setActionError(null);

    try {
      const res = await deleteBanner({ variables: { id: toDelete.id } });
      const errors = res.data?.deleteNewsletterBanner?.errors || [];
      if (errors.length) {
        setActionError(errors.join(", "));
        return;
      }

      setConfirmOpen(false);
      setToDelete(null);
    } catch (e: any) {
      setActionError(e.message || "Failed to delete banner");
    }
  };

  return (
    <>
      <AdminLoading open={busy} />

      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          mb={2}
          spacing={2}
        >
          <Typography variant="h5">Newsletter Banner</Typography>

          <Button
            variant="contained"
            startIcon={hasBanner ? <EditIcon /> : <AddIcon />}
            onClick={openUploadOrEdit}
          >
            {hasBanner ? "Edit Newsletter Banner" : "Upload Newsletter Banner"}
          </Button>
        </Stack>
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2,
            alignItems: "flex-start",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            Newsletter Banner Image Guidelines
          </Typography>

          <Typography variant="body2" sx={{ mt: 0.5 }}>
            • <strong>Required size:</strong> 1600 × 600 pixels (landscape)
            <br />
            • Keep important content centered
            <br />
            • Avoid placing text near the edges
            <br />• Only one banner is allowed at a time
            <br />• Newsletter page can be accessed at: /newsletter
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load banner: {error.message}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Image</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {!currentBanner && !loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No newsletter banner uploaded yet.
                    </TableCell>
                  </TableRow>
                ) : currentBanner ? (
                  <TableRow hover>
                    <TableCell>
                      {currentBanner.image ? (
                        <img
                          src={buildNewsletterBannerImageUrl(
                            currentBanner.image,
                          )}
                          alt="newsletter banner"
                          style={{
                            width: 220,
                            height: 72,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "1px solid rgba(0,0,0,0.1)",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No image
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {currentBanner.updatedAt
                          ? new Date(currentBanner.updatedAt).toLocaleString()
                          : "-"}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <IconButton size="small" onClick={openUploadOrEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        color="error"
                        onClick={askDelete}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <NewsletterBannerFormDialog
        open={dialogOpen}
        banner={dialogOpen ? form : null}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Delete newsletter banner?"
        description="Are you sure you want to delete the newsletter banner? You can upload a new one later."
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default NewsletterBannerAdminPage;
